import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  fetchNoticeDetails,
  fetchNoticeAdditionalData,
  downloadNoticeDocument,
  downloadClarification,
  downloadErrata,
  triggerFileDownload,
} from '../../services/publicTendersApi';
import pdfIcon from '../../assets/icons/pdf-icon.png';
import docIcon from '../../assets/icons/doc-icon.png';
import xlsIcon from '../../assets/icons/xls-icon.png';
import zipIcon from '../../assets/icons/zip-icon.png';
import defaultIcon from '../../assets/icons/file-icon.png';
import errataIcon from '../../assets/icons/errata-link.png';
import './AuctionDetails.css';
import Utility from './../../utils/Utility';
import SeoPageComponent from '../../components/SeoPageComponent/SeoPageComponent';
import Breadcrumbs from '../../components/Breadcrumbs';
import RestrictedAccess from '../../components/RestrictedAccess/RestrictedAccess';
import GenerateOfferButton from '../../components/GenerateOfferButton/GenerateOfferButton';
import CDERequestButton from '../../components/CDERequestButton/CDERequestButton';
import PremiumAIReport from '../../components/PremiumAIReport/PremiumAIReport';
import ProjectBadge from '../../components/ProjectBadge/ProjectBadge';
import ConversionPopup from '../../components/ConversionPopup/ConversionPopup';
import ProcedurePaywall from '../../components/ProcedurePaywall/ProcedurePaywall';
import { slugify } from '../../utils/slugify';
import { useAuth } from '../../context/AuthContext';
import SaveProcedureButton from '../../components/SaveProcedureButton/SaveProcedureButton';
import FileDownloadButton from '../../components/FileDownloadButton/FileDownloadButton';
import TruncatedText from '../../components/TruncatedText/TruncatedText';

/**
 * AuctionDetails_RFD - Component pentru Request for Documentation (RFD)
 * NOTĂ: Folosește structură generică (fără date MongoDB specifice încă)
 * Colecție MongoDB: Collections_RFD
 */
const AuctionDetails_RFD = ({ noticeNo: noticeNoProp, procedureType, prefetchedDetails, mongoId }) => {
  const { noticeNo: noticeNoParam } = useParams();
  const noticeNo = noticeNoProp || noticeNoParam;
  const id = mongoId || noticeNoParam; // mongoId for API calls
  const [details, setDetails] = useState(prefetchedDetails || null);
  const [loading, setLoading] = useState(!prefetchedDetails);
  const [loadingAdditionalData, setLoadingAdditionalData] = useState(false);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [showConversionPopup, setShowConversionPopup] = useState(false);

  // Verificăm dacă utilizatorul este autentificat
  const { isAuthenticated } = useAuth();

  useEffect(() => {
    // Reset semnal la schimbarea rutei
    if (typeof window !== 'undefined') window.prerenderReady = false;
  }, [noticeNo]);

  // Fallback: nu bloca snapshot-ul după 10 secunde
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }, 10000);
    return () => clearTimeout(timeout);
  }, [noticeNo]);

  // Setează prerenderReady când loading se termină (cu succes sau eroare)
  useEffect(() => {
    if (!loading) {
      if (typeof window !== 'undefined') window.prerenderReady = true;
    }
  }, [loading, details, notFound, error]);

  // Afișează popup-ul de conversie pentru utilizatorii neautentificați
  useEffect(() => {
    if (!loading && !error && !notFound && details && !isAuthenticated) {
      const timer = setTimeout(() => {
        setShowConversionPopup(true);
      }, 5000); // Afișează popup-ul după 5 secunde

      return () => clearTimeout(timer);
    }
  }, [loading, error, notFound, details, isAuthenticated]);

  // Main details fetch — only runs if the router did NOT prefetch
  useEffect(() => {
    if (prefetchedDetails) return;

    let mounted = true;
    const getDetails = async () => {
      try {
        setLoading(true);
        setError(null);
        setNotFound(false);
        const data = await fetchNoticeDetails(id);
        if (!mounted) return;
        if (!data) {
          setNotFound(true);
          return;
        }
        setDetails(data);
      } catch (err) {
        if (!mounted) return;
        if (err.message?.includes('404') || err.status === 404) {
          setNotFound(true);
        } else {
          setError(err.message || 'Something went wrong');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    getDetails();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Additional data fetch (files, clarifications, errata) — only for subscribers
  useEffect(() => {
    if (!details || details.isPartial) return;
    if (details.dfNoticeSectionFiles !== null && details.dfNoticeSectionFiles !== undefined) return; // already loaded

    let mounted = true;
    const loadAdditional = async () => {
      setLoadingAdditionalData(true);
      try {
        const realNoticeNo = details.specificView?.noticeNo || noticeNo;
        if (!realNoticeNo) return;
        const additionalData = await fetchNoticeAdditionalData(realNoticeNo);
        if (!mounted) return;
        setDetails((prev) => ({
          ...prev,
          dfNoticeSectionFiles: additionalData.dfNoticeSectionFiles,
          clarifications: additionalData.clarifications,
          errata: additionalData.errata,
        }));
      } catch (err) {
        console.error('Error loading additional data:', err);
      } finally {
        if (mounted) setLoadingAdditionalData(false);
      }
    };
    loadAdditional();
    return () => { mounted = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [details?.specificView?.noticeNo, details?.isPartial]);

  const parseDateForRFD = (dateString) => {
    if (!dateString) {
      return 'N/A';
    }

    try {
      let date;

      // Format timestamp numeric (ms sau s)
      if (typeof dateString === 'number') {
        // Presupunem că e în milisecunde dacă e mai mare de 1 miliard
        if (dateString > 1000000000) {
          date = new Date(dateString);
        } else {
          date = new Date(dateString * 1000);
        }
      }
      // Format string numeric
      else if (/^\d+$/.test(dateString)) {
        const num = parseInt(dateString);
        if (num > 1000000000) {
          date = new Date(num);
        } else {
          date = new Date(num * 1000);
        }
      }
      // Format ISO: YYYY-MM-DD sau YYYY-MM-DDTHH:mm:ss
      else if (dateString.includes('-') && !dateString.includes('.')) {
        date = new Date(dateString);
      }
      // Format DD.MM.YYYY
      else if (dateString.includes('.')) {
        const [day, month, year] = dateString.split('.');
        date = new Date(`${year}-${month}-${day}`);
      }
      // Format DD/MM/YYYY
      else if (dateString.includes('/')) {
        const [day, month, year] = dateString.split('/');
        date = new Date(`${year}-${month}-${day}`);
      } else {
        date = new Date(dateString);
      }

      if (isNaN(date.getTime())) {
        return 'N/A';
      }

      const options = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      };

      return date.toLocaleDateString('ro-RO', options);
    } catch {
      return 'N/A';
    }
  };

  const getFileIcon = (extension) => {
    switch (extension) {
      case 'pdf':
        return pdfIcon;
      case 'doc':
      case 'docx':
        return docIcon;
      case 'xls':
      case 'xlsx':
        return xlsIcon;
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
        return zipIcon;
      default:
        return defaultIcon;
    }
  };

  const handleFileDownload = async (doc) => {
    const { noticeDocumentUrl, noticeDocumentName } = doc;
    const cNoticeId = details.specificView?.cNoticeId;
    const noticeNo = details.specificView?.noticeNo;

    if (!noticeDocumentUrl || !noticeDocumentName || !cNoticeId || !noticeNo) {
      console.error('Missing data for file download.');
      alert('Some required information is missing. Please try again.');
      return;
    }

    try {
      const { blob, fileName } = await downloadNoticeDocument(
        noticeDocumentUrl,
        noticeDocumentName,
        cNoticeId,
        noticeNo,
      );
      triggerFileDownload(blob, fileName);
    } catch (error) {
      console.error('Error during file download:', error);
      alert('An error occurred while attempting to download the file.');
    }
  };

  const handleClarificationDownload = async (doc, cNoticeId, noticeNo) => {
    const { noticeDocumentUrl, documentName } = doc;

    try {
      const { blob, fileName } = await downloadClarification(
        noticeDocumentUrl,
        documentName,
        cNoticeId,
        noticeNo,
      );
      triggerFileDownload(blob, fileName);
    } catch (error) {
      console.error('Error downloading clarification:', error);
      alert('A apărut o eroare la descărcarea clarificării.');
    }
  };

  // SEO constants pentru toate cazurile
  const titleSlug = slugify(details?.stickyData?.contractTitle);
  const canonical = `${import.meta.env.VITE_DOMAIN}/licitatii/${titleSlug}/${encodeURIComponent(id)}`;
  const defaultTitle = 'Licitatii-Constructii – Licitații în construcții';
  const defaultDesc =
    'Caută și urmărește licitații publice din construcții pe Licitatii-Constructii.';
  const title = details?.stickyData?.contractTitle
    ? `${details.stickyData.contractTitle} – Licitatii-Constructii`
    : defaultTitle;
  const description = details?.stickyData?.contractTitle
    ? `Informații despre licitația ${details.stickyData.contractTitle}, publicată de ${details.stickyData?.caName || 'N/A'}.`
    : defaultDesc;
  const ogImage = details?.imageUrl || `${import.meta.env.VITE_DOMAIN}/og-default.png`;

  if (notFound) {
    return (
      <>
        <SeoPageComponent
          pageTitle="Licitație indisponibilă – Licitatii-Constructii"
          pageDescription="Această licitație nu mai este disponibilă sau a fost mutată."
          pageUrl={canonical}
          logoUrl={ogImage}
          keywords="licitatii, go4projects"
          canonicalUrl={canonical}
          robotsContent="index,follow"
        />
        <main className="container mx-auto px-4 py-8">
          <h1 className="text-2xl font-bold text-gray-800 mb-4">
            Licitație indisponibilă
          </h1>
          <p className="text-gray-600">Cod: {noticeNo}</p>
          <p className="text-gray-600 mt-2">
            Această licitație nu mai este disponibilă sau a fost mutată.
          </p>
        </main>
      </>
    );
  }

  if (loading) {
    return (
      <>
        <SeoPageComponent
          pageTitle={title}
          pageDescription={description}
          pageUrl={canonical}
          logoUrl={ogImage}
          keywords={`licitatii, constructii, ${details?.stickyData?.caName || ''}, ${noticeNo}`}
          canonicalUrl={canonical}
        />
        <main className="container mx-auto px-4 py-8">
          <div style={{ padding: '2rem 0' }}>Se încarcă…</div>
        </main>
      </>
    );
  }

  if (error) {
    return (
      <>
        <SeoPageComponent
          pageTitle="Eroare – Licitatii-Constructii"
          pageDescription="A apărut o eroare la încărcarea licitației."
          pageUrl={canonical}
          logoUrl={ogImage}
          keywords="licitatii, go4projects"
          canonicalUrl={canonical}
        />
        <main className="container mx-auto px-4 py-8">
          <p className="error-text">Eroare: {error}</p>
        </main>
      </>
    );
  }

  if (!details) {
    return (
      <>
        <SeoPageComponent
          pageTitle={title}
          pageDescription={description}
          pageUrl={canonical}
          logoUrl={ogImage}
          keywords="licitatii, go4projects"
          canonicalUrl={canonical}
        />
        <main className="container mx-auto px-4 py-8">
          <p>Nu există date disponibile.</p>
        </main>
      </>
    );
  }

  const breadcrumbLinks = [
    { href: '/', label: 'Home' },
    { href: '/licitatii', label: 'Licitatii' },
    { label: details.stickyData?.contractTitle || 'N/A' },
  ];

  return (
    <>
      <SeoPageComponent
        pageTitle={title}
        pageDescription={description}
        pageUrl={canonical}
        logoUrl={ogImage}
        keywords={`licitatii, constructii, ${details.stickyData?.caName || ''}, ${noticeNo}`}
        canonicalUrl={canonical}
      />
      <Breadcrumbs breadcrumbLinks={breadcrumbLinks} />
      {/* Titlu complet, vizibil, deasupra summary bar */}
      <section className="pt-10 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <h1 className="text-2xl md:text-4xl font-extrabold text-indigo-700 text-center mb-4 break-words drop-shadow-xl">
            {details.stickyData?.contractTitle || 'Detalii Licitație'}
          </h1>
        </div>
      </section>
      {/* Summary bar compact, smart, vizibil rapid */}
      <section className="pb-6 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="flex flex-wrap justify-center md:justify-between items-center gap-2 md:gap-4 bg-white/80 rounded-xl shadow-lg px-4 py-4 md:py-6 mb-6 border border-indigo-100">
            <div className="flex flex-wrap gap-2 justify-center items-center w-full md:w-auto">
              <span className="bg-gray-100 text-gray-700 text-xs md:text-sm font-bold px-3 py-1 rounded-full border border-gray-300 shadow-sm">
                Invitație de depunere ofertă la sistem de achiziții dinamic
                (RFD)
              </span>
              {!details.isPartial && (
                <span className="bg-green-100 text-green-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-green-200 shadow-sm">
                  {details.stickyData?.noticeNumber || 'N/A'}
                </span>
              )}
              {!details.isPartial && (
                <span className="bg-yellow-100 text-yellow-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-yellow-200 shadow-sm">
                  {details.stickyData?.caName || 'N/A'}
                </span>
              )}
              <span className="bg-purple-100 text-purple-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-purple-200 shadow-sm">
                {details.stickyData?.sysProcedureType || 'N/A'}
              </span>
              <span className="bg-indigo-100 text-indigo-700 text-xs md:text-sm font-bold px-3 py-1 rounded-full border border-indigo-200 shadow-sm">
                {Utility.ConvertEstimatedValue(
                  details.section21View.estimatedValue,
                )}{' '}
                {details.section21View.currencyCode}
              </span>
              {details.section21View?.mainCpvCode && (
                <span className="bg-blue-100 text-blue-700 text-xs md:text-sm font-semibold px-3 py-1 rounded-full border border-blue-200 shadow-sm">
                  CPV: {details.section21View.mainCpvCode}
                </span>
              )}
            </div>
            <div className="w-full md:w-auto flex justify-center md:justify-end mt-2 md:mt-0">
              <SaveProcedureButton noticeNo={noticeNo} />
            </div>
          </div>
        </div>
      </section>

      {/* Detalii compacte, organizate pe grid */}
      <section className="pb-16 bg-gradient-to-br from-indigo-50 via-white to-green-100">
        <div className="container w-full max-w-full px-2 md:px-8 mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Info cheie */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Detalii licitație
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                  <span className="font-medium">Titlu:</span>
                  <span
                    className="truncate"
                    title={details.stickyData?.contractTitle}
                  >
                    {details.stickyData?.contractTitle || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-500"></span>
                  <span className="font-medium">Număr:</span>

                  <span className="text-green-700 font-semibold bg-green-50 px-2 py-0.5 rounded text-sm">
                    {details.stickyData?.noticeNumber || 'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-yellow-500"></span>
                  <span className="font-medium">Autoritate:</span>
                  <RestrictedAccess
                    compact={true}
                    message="Informatie despre autoritate contractanta disponibila doar pentru utilizatorii autentificati"
                  >
                    <span className="text-yellow-700 font-semibold bg-yellow-50 px-2 py-0.5 rounded text-sm">
                      {details.stickyData?.caName || 'N/A'}
                    </span>
                  </RestrictedAccess>
                </div>
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                  <span className="font-medium">Tip procedură:</span>
                  <span className="text-purple-700 font-semibold bg-purple-50 px-2 py-0.5 rounded text-sm">
                    {details.stickyData?.sysProcedureType || 'N/A'}
                  </span>
                </div>
              </div>
            </div>
            {/* Termene + Coduri CPV unificate */}
            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Termene & Coduri CPV
              </h2>
              <div className="flex flex-col gap-2 text-base text-gray-700">
                <div className="flex items-center gap-2">
                  <span className="font-medium">Valabilitate ofertă:</span>
                  <RestrictedAccess
                    compact={true}
                    message="Data limita de depunere disponibila doar pentru utilizatorii autentificati"
                  >
                    <span>
                      {parseDateForRFD(
                        details.section4View?.tenderAvailabilityDeadline,
                      )}
                    </span>
                  </RestrictedAccess>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Publicare:</span>
                  <span>
                    {Utility.FormatDate(details.stickyData?.publicationDate) ||
                      'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-medium">Termen limită:</span>
                  <span>
                    {details.section4View?.tenderReceiptDeadline ||
                      details.section4View?.tenderOpeningDate ||
                      'N/A'}
                  </span>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="font-medium">Cod CPV principal:</span>
                  <span>{details.section21View?.mainCpvCode || 'N/A'}</span>
                </div>
              </div>
            </div>

            {/* Servicii premium */}
            <div className="col-span-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <GenerateOfferButton auctionId={noticeNo} variant="section" />
              <CDERequestButton auctionId={noticeNo} variant="section" />
              <PremiumAIReport noticeNo={noticeNo} type="auction" />
              <ProjectBadge auctionId={noticeNo} variant="section" />
            </div>

            {/* Localizare și istoric */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
                <h2 className="text-indigo-500 text-base font-bold mb-2">
                  Localizare și Contact
                </h2>
                <div className="flex flex-col gap-2 text-base text-gray-700">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-blue-500"></span>
                    <span className="font-medium">Autoritate:</span>
                    <span className="text-blue-700 font-semibold bg-blue-50 px-2 py-0.5 rounded text-sm">
                      {details.stickyData?.caName || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="font-medium">Număr anunț:</span>
                    <span className="text-green-700 font-semibold bg-green-50 px-2 py-0.5 rounded text-sm">
                      {details.stickyData?.noticeNumber || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-purple-500"></span>
                    <span className="font-medium">Status:</span>
                    <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                      Activă
                    </span>
                  </div>
                </div>
              </div>
              <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
                <h2 className="text-indigo-500 text-base font-bold mb-2">
                  Istoric și Etape
                </h2>
                <div className="flex flex-col gap-2 text-base text-gray-700">
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-green-500"></span>
                    <span className="font-medium">Publicat:</span>
                    <span>
                      {Utility.FormatDate(
                        details.stickyData?.publicationDate,
                      ) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-orange-500"></span>
                    <span className="font-medium">Tip procedură:</span>
                    <span className="text-orange-700 font-semibold bg-orange-50 px-2 py-0.5 rounded text-sm">
                      {details.stickyData?.sysProcedureType || 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-indigo-500"></span>
                    <span className="font-medium">Valoare:</span>
                    <span className="text-indigo-700 font-bold bg-indigo-50 px-2 py-0.5 rounded text-sm">
                      {Utility.ConvertEstimatedValue(
                        details.section21View.estimatedValue,
                      )}{' '}
                      {details.section21View.currencyCode}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Fisiere disponibile, clarificari, errata, criterii etc. */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {/* Fisiere disponibile */}
              <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
                <h2 className="text-indigo-500 text-base font-bold mb-2">
                  Fișiere disponibile
                  {loadingAdditionalData && (
                    <span className="ml-2 text-xs text-gray-400 animate-pulse">
                      Se încarcă...
                    </span>
                  )}
                </h2>
                <RestrictedAccess message="Sectiunea fisiere disponibila doar pentru utilizatorii autentificati">
                  <div className="space-y-2">
                    {loadingAdditionalData ? (
                      <div className="flex items-center gap-2 text-gray-400 text-xs">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                        Se încarcă fișierele...
                      </div>
                    ) : details.dfNoticeSectionFiles?.dfNoticeDocs?.length >
                      0 ? (
                      details.dfNoticeSectionFiles.dfNoticeDocs.map(
                        (doc, index) => {
                          const fileName =
                            (doc.noticeDocumentName || '').replace(
                              /\.p7m|\.p7s$/,
                              '',
                            ) || 'Document';
                          return (
                            <FileDownloadButton
                              key={index}
                              fileName={fileName}
                              onDownload={() => handleFileDownload(doc)}
                            />
                          );
                        },
                      )
                    ) : (
                      <p className="text-xs text-gray-500">
                        Nu există fișiere disponibile.
                      </p>
                    )}
                  </div>
                </RestrictedAccess>
              </div>
              {/* Clarificari si errata */}
              <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
                <h2 className="text-indigo-500 text-base font-bold mb-2">
                  Clarificări & Errata
                  {loadingAdditionalData && (
                    <span className="ml-2 text-xs text-gray-400 animate-pulse">
                      Se încarcă...
                    </span>
                  )}
                </h2>
                <div className="flex flex-col gap-2">
                  {/* Clarificari */}
                  <div>
                    <h3 className="text-indigo-400 text-xs font-semibold mb-1">
                      Clarificări
                    </h3>
                    <RestrictedAccess message="Sectiunea clarificari disponibila doar pentru utilizatorii autentificati">
                      <div className="space-y-1">
                        {loadingAdditionalData ? (
                          <div className="flex items-center gap-2 text-gray-400 text-xs">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                            Se încarcă clarificările...
                          </div>
                        ) : details.clarifications?.items?.length > 0 ? (
                          details.clarifications.items.map((doc, index) => {
                            const fileName =
                              (doc.documentName || '').replace(/\.p7m|\.p7s$/, '') ||
                              'Document';
                            return (
                              <FileDownloadButton
                                key={index}
                                fileName={fileName}
                                onDownload={() =>
                                  handleClarificationDownload(
                                    doc,
                                    details.specificView?.cNoticeId,
                                    details.specificView?.noticeNo,
                                  )
                                }
                              />
                            );
                          })
                        ) : (
                          <p className="text-xs text-gray-500">
                            Nu există clarificări disponibile.
                          </p>
                        )}
                      </div>
                    </RestrictedAccess>
                  </div>
                  {/* Errata */}
                  <div>
                    <h3 className="text-indigo-400 text-xs font-semibold mb-1">
                      Errata
                    </h3>
                    <RestrictedAccess message="Sectiunea errata este disponibila doar pentru utilizatorii autentificati">
                      <div className="space-y-1">
                        {loadingAdditionalData ? (
                          <div className="flex items-center gap-2 text-gray-400 text-xs">
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-indigo-500"></div>
                            Se încarcă errata...
                          </div>
                        ) : details.errata?.items?.length > 0 ? (
                          details.errata.items.map((errataItem, index) => {
                            const errataUrl = `https://www.e-licitatie.ro/pub/notices/e-notice/v2/view/${errataItem.eNoticeID}`;
                            return (
                              <div
                                key={index}
                                className="flex items-center gap-2 border border-slate-200 rounded p-2 bg-slate-50 hover:shadow transition"
                              >
                                <img
                                  src={getFileIcon(errataIcon)}
                                  alt="errata icon"
                                  className="w-6 h-6"
                                />
                                <a
                                  href={errataUrl}
                                  className="text-indigo-600 font-semibold hover:underline text-xs truncate"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  {errataItem.eNoticeNO}
                                </a>
                              </div>
                            );
                          })
                        ) : (
                          <p className="text-xs text-gray-500">
                            Nu există errate disponibile.
                          </p>
                        )}
                      </div>
                    </RestrictedAccess>
                  </div>
                </div>
              </div>
              {/* Valori financiare */}
              <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-4">
                <h2 className="text-indigo-500 text-base font-bold mb-2">
                  Valori financiare
                </h2>
                <div className="flex flex-col gap-2 text-base text-gray-700">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Valoare estimată:</span>
                    <span className="text-green-700 font-bold bg-green-50 px-2 py-0.5 rounded text-sm">
                      {details.section21View?.estimatedValue
                        ? `${Utility.ConvertEstimatedValue(
                            details.section21View.estimatedValue,
                          )} ${details.section21View.currencyCode || ''}`
                        : 'N/A'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Garanție participare:</span>
                    <span>
                      {details.section6View?.depositsAndWarranties || 'N/A'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Informații sistem de achiziții dinamic */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-2">
                <h2 className="text-indigo-500 text-base font-bold mb-2">
                  Cerințe înscriere sistem
                </h2>
                <TruncatedText text={details.section3View?.depositsAndWarranties ||
                    details.section3View?.personalSituation ||
                    'N/A'} />
              </div>
              <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-2">
                <h2 className="text-indigo-500 text-base font-bold mb-2">
                  Calificări necesare
                </h2>
                <TruncatedText text={details.section3View?.tpCriteriaQAStandardMin || 'N/A'} />
              </div>
            </div>

            <div className="bg-white shadow rounded-xl p-5 flex flex-col gap-2 mt-6">
              <h2 className="text-indigo-500 text-base font-bold mb-2">
                Informații sistem dinamic
              </h2>
              <TruncatedText text={details.section6View?.additionalInformation || 'N/A'} />
            </div>
          </div>
        </div>
      </section>

      {/* Paywall pentru utilizatorii fără abonament */}
      {details.isPartial && <ProcedurePaywall />}

      {/* Popup de conversie pentru utilizatorii neautentificați */}
      <ConversionPopup
        isOpen={showConversionPopup}
        onClose={() => setShowConversionPopup(false)}
        onSuccess={() => {
          // După înregistrarea cu succes, reîmprospătăm pagina
          window.location.reload();
        }}
      />
    </>
  );
};

export default AuctionDetails_RFD;
